import React, { useEffect, useState } from "react";
import api from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../components/ui/tabs";
import { toast } from "sonner";
import { Sparkles, Save, RefreshCw, Trash2, Plus, ExternalLink, Search } from "lucide-react";

export default function AdminSEO() {
    const [settings, setSettings] = useState({
        site_meta_title: "",
        site_meta_description: "",
        og_image_url: "",
        twitter_handle: "",
        robots_txt: "",
        ga4_measurement_id: "",
        gtm_container_id: "",
        meta_pixel_id: "",
    });
    const [pages, setPages] = useState([]);
    const [newPage, setNewPage] = useState({ path: "", title: "", description: "", og_image: "" });
    const [saving, setSaving] = useState(false);
    const [keywordSeed, setKeywordSeed] = useState("");
    const [keywordIdeas, setKeywordIdeas] = useState(null);
    const [aiBusy, setAiBusy] = useState(false);
    const [metaCtx, setMetaCtx] = useState("");
    const [metaAiBusy, setMetaAiBusy] = useState(false);

    const load = async () => {
        const s = await api.get("/admin/seo");
        setSettings((prev) => ({ ...prev, ...(s.data.settings || {}) }));
        const p = await api.get("/admin/seo/pages");
        setPages(p.data.rows || []);
    };
    useEffect(() => { load(); }, []);

    const save = async () => {
        setSaving(true);
        try {
            await api.put("/admin/seo", settings);
            toast.success("SEO settings saved");
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Save failed");
        } finally { setSaving(false); }
    };

    const upsertPage = async () => {
        if (!newPage.path.startsWith("/")) { toast.error("Path must start with /"); return; }
        await api.put("/admin/seo/pages", newPage);
        toast.success("Page meta saved");
        setNewPage({ path: "", title: "", description: "", og_image: "" });
        load();
    };
    const deletePage = async (path) => {
        if (!window.confirm(`Remove meta for ${path}?`)) return;
        await api.delete("/admin/seo/pages", { params: { path } });
        load();
    };

    const suggestKeywords = async () => {
        if (!keywordSeed.trim()) { toast.error("Enter a topic first"); return; }
        setAiBusy(true);
        try {
            const { data } = await api.post("/admin/seo/keyword-ideas", { seed: keywordSeed });
            setKeywordIdeas(data);
            toast.success("Keyword ideas ready");
        } catch (e) {
            toast.error(e.response?.data?.detail || "AI request failed");
        } finally { setAiBusy(false); }
    };

    const writeMeta = async () => {
        if (!metaCtx.trim()) { toast.error("Describe the page first"); return; }
        setMetaAiBusy(true);
        try {
            const { data } = await api.post("/admin/seo/write-meta", { context: metaCtx });
            setSettings((prev) => ({
                ...prev,
                site_meta_title: data.meta_title || prev.site_meta_title,
                site_meta_description: data.meta_description || prev.site_meta_description,
            }));
            toast.success("AI meta filled — remember to Save");
        } catch (e) {
            toast.error(e.response?.data?.detail || "AI request failed");
        } finally { setMetaAiBusy(false); }
    };

    const set = (k, v) => setSettings((prev) => ({ ...prev, [k]: v }));

    return (
        <div className="space-y-6" data-testid="admin-seo-page">
            <div>
                <div className="text-[10px] uppercase tracking-widest text-sky-700">Search visibility</div>
                <h1 className="font-serif text-4xl text-stone-900">SEO</h1>
                <p className="text-sm text-stone-500 mt-1">Meta tags, robots policy, tracking IDs, and AI-assisted keyword research. <a className="text-sky-700 underline" target="_blank" rel="noreferrer" href="/api/sitemap.xml">View sitemap.xml <ExternalLink className="w-3 h-3 inline" /></a> · <a className="text-sky-700 underline" target="_blank" rel="noreferrer" href="/api/robots.txt">robots.txt <ExternalLink className="w-3 h-3 inline" /></a></p>
            </div>

            <Tabs defaultValue="meta">
                <TabsList className="rounded-none">
                    <TabsTrigger value="meta" className="rounded-none" data-testid="seo-tab-meta">Site meta</TabsTrigger>
                    <TabsTrigger value="tracking" className="rounded-none" data-testid="seo-tab-tracking">Tracking IDs</TabsTrigger>
                    <TabsTrigger value="pages" className="rounded-none" data-testid="seo-tab-pages">Per-page overrides</TabsTrigger>
                    <TabsTrigger value="robots" className="rounded-none" data-testid="seo-tab-robots">Robots.txt</TabsTrigger>
                    <TabsTrigger value="keywords" className="rounded-none" data-testid="seo-tab-keywords">AI keyword ideas</TabsTrigger>
                </TabsList>

                {/* Site meta */}
                <TabsContent value="meta" className="mt-4 space-y-4">
                    <div className="bg-white border border-stone-200 p-5 space-y-4">
                        <Field label="Site meta title" value={settings.site_meta_title} onChange={(v) => set("site_meta_title", v)} placeholder="Hahnemann Pharmacy & Laboratory — Homeopathic medicines online" testId="seo-meta-title" max={70} />
                        <Field label="Site meta description" textarea value={settings.site_meta_description} onChange={(v) => set("site_meta_description", v)} placeholder="Buy authentic homeopathic medicines online, consult verified homeopaths, distributor pricing available." testId="seo-meta-desc" max={180} />
                        <Field label="Open Graph image URL" value={settings.og_image_url} onChange={(v) => set("og_image_url", v)} placeholder="https://…/og-image.jpg" testId="seo-og-image" />
                        <Field label="Twitter handle" value={settings.twitter_handle} onChange={(v) => set("twitter_handle", v)} placeholder="@hahnemannpharma" testId="seo-twitter" />
                        <div className="border-t border-stone-200 pt-4">
                            <div className="text-xs uppercase tracking-widest text-stone-500 mb-2 flex items-center gap-2"><Sparkles className="w-3 h-3 text-purple-600" />Ask AI to write meta for a page</div>
                            <Textarea value={metaCtx} onChange={(e) => setMetaCtx(e.target.value)} placeholder="Describe the page in one line — e.g. 'Homeopathic mother tincture catalogue for chronic acidity, priced from ₹35'" rows={2} className="rounded-none border-stone-300" data-testid="seo-ai-meta-context" />
                            <Button onClick={writeMeta} disabled={metaAiBusy} className="rounded-none bg-purple-700 hover:bg-purple-800 text-stone-50 mt-2" data-testid="seo-ai-meta-btn">
                                <Sparkles className="w-4 h-4 mr-2" />{metaAiBusy ? "Thinking…" : "Fill meta with AI"}
                            </Button>
                        </div>
                    </div>
                    <Button onClick={save} disabled={saving} className="rounded-none bg-sky-700 hover:bg-sky-800 text-stone-50" data-testid="seo-save-btn">
                        <Save className="w-4 h-4 mr-2" />{saving ? "Saving…" : "Save SEO settings"}
                    </Button>
                </TabsContent>

                {/* Tracking */}
                <TabsContent value="tracking" className="mt-4 space-y-4">
                    <div className="bg-white border border-stone-200 p-5 space-y-4">
                        <p className="text-xs text-stone-500">These IDs are injected into every public page's <code>&lt;head&gt;</code>. Leave blank to disable.</p>
                        <Field label="Google Analytics 4 measurement ID" value={settings.ga4_measurement_id} onChange={(v) => set("ga4_measurement_id", v)} placeholder="G-XXXXXXXXXX" testId="seo-ga4" />
                        <Field label="Google Tag Manager container ID" value={settings.gtm_container_id} onChange={(v) => set("gtm_container_id", v)} placeholder="GTM-XXXXXXX" testId="seo-gtm" />
                        <Field label="Meta Pixel ID" value={settings.meta_pixel_id} onChange={(v) => set("meta_pixel_id", v)} placeholder="123456789012345" testId="seo-meta-pixel" />
                    </div>
                    <Button onClick={save} disabled={saving} className="rounded-none bg-sky-700 hover:bg-sky-800 text-stone-50" data-testid="seo-save-tracking-btn">
                        <Save className="w-4 h-4 mr-2" />{saving ? "Saving…" : "Save tracking IDs"}
                    </Button>
                </TabsContent>

                {/* Per-page overrides */}
                <TabsContent value="pages" className="mt-4 space-y-4">
                    <div className="bg-white border border-stone-200 p-5 space-y-3">
                        <div className="text-xs uppercase tracking-widest text-stone-500 mb-2">Add / update per-page meta</div>
                        <div className="grid md:grid-cols-4 gap-2">
                            <Input placeholder="/shop" value={newPage.path} onChange={(e) => setNewPage((p) => ({ ...p, path: e.target.value }))} className="rounded-none border-stone-300" data-testid="seo-page-path" />
                            <Input placeholder="Meta title" value={newPage.title} onChange={(e) => setNewPage((p) => ({ ...p, title: e.target.value }))} className="rounded-none border-stone-300" data-testid="seo-page-title" />
                            <Input placeholder="Meta description" value={newPage.description} onChange={(e) => setNewPage((p) => ({ ...p, description: e.target.value }))} className="rounded-none border-stone-300" data-testid="seo-page-desc" />
                            <Input placeholder="OG image URL" value={newPage.og_image} onChange={(e) => setNewPage((p) => ({ ...p, og_image: e.target.value }))} className="rounded-none border-stone-300" data-testid="seo-page-og" />
                        </div>
                        <Button onClick={upsertPage} className="rounded-none bg-sky-700 hover:bg-sky-800 text-stone-50" data-testid="seo-page-save">
                            <Plus className="w-4 h-4 mr-2" />Save page meta
                        </Button>
                    </div>
                    <div className="bg-white border border-stone-200">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="bg-stone-50 border-b border-stone-200">
                                    <th className="text-left p-3 text-xs uppercase tracking-widest text-stone-500">Path</th>
                                    <th className="text-left p-3 text-xs uppercase tracking-widest text-stone-500">Title</th>
                                    <th className="text-left p-3 text-xs uppercase tracking-widest text-stone-500">Description</th>
                                    <th className="w-20"></th>
                                </tr>
                            </thead>
                            <tbody>
                                {pages.length === 0 ? (
                                    <tr><td colSpan={4} className="p-6 text-center text-stone-400">No per-page overrides yet. The site meta above is used for every page by default.</td></tr>
                                ) : pages.map((p) => (
                                    <tr key={p.path} className="border-b border-stone-100" data-testid={`seo-page-row-${p.path}`}>
                                        <td className="p-3 font-mono">{p.path}</td>
                                        <td className="p-3">{p.title}</td>
                                        <td className="p-3 text-xs text-stone-600">{p.description}</td>
                                        <td className="p-3 text-right">
                                            <button onClick={() => deletePage(p.path)} className="text-red-600 hover:bg-red-50 p-1.5 border border-red-200" data-testid={`seo-page-del-${p.path}`}>
                                                <Trash2 className="w-4 h-4" />
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </TabsContent>

                {/* Robots.txt */}
                <TabsContent value="robots" className="mt-4 space-y-4">
                    <div className="bg-white border border-stone-200 p-5 space-y-3">
                        <div className="text-xs uppercase tracking-widest text-stone-500">robots.txt override</div>
                        <Textarea rows={12} value={settings.robots_txt || ""} onChange={(e) => set("robots_txt", e.target.value)} className="rounded-none border-stone-300 font-mono text-xs" placeholder="Leave blank for the defaults" data-testid="seo-robots-body" />
                        <p className="text-xs text-stone-500">AI crawler policies configured in the GEO tab are appended automatically.</p>
                    </div>
                    <Button onClick={save} disabled={saving} className="rounded-none bg-sky-700 hover:bg-sky-800 text-stone-50" data-testid="seo-save-robots-btn">
                        <Save className="w-4 h-4 mr-2" />{saving ? "Saving…" : "Save robots.txt"}
                    </Button>
                </TabsContent>

                {/* Keywords AI */}
                <TabsContent value="keywords" className="mt-4 space-y-4">
                    <div className="bg-white border border-stone-200 p-5 space-y-3">
                        <div className="text-xs uppercase tracking-widest text-stone-500 flex items-center gap-2"><Search className="w-3 h-3" />AI keyword research</div>
                        <div className="flex gap-2">
                            <Input value={keywordSeed} onChange={(e) => setKeywordSeed(e.target.value)} placeholder="e.g. Arnica 30 for muscle pain, or ‘homeopathy for kids cough’" className="rounded-none border-stone-300 flex-1" data-testid="seo-keyword-seed" />
                            <Button onClick={suggestKeywords} disabled={aiBusy} className="rounded-none bg-purple-700 hover:bg-purple-800 text-stone-50" data-testid="seo-keyword-btn">
                                <Sparkles className="w-4 h-4 mr-2" />{aiBusy ? "Thinking…" : "Suggest"}
                            </Button>
                        </div>
                        {keywordIdeas && (
                            <div className="grid md:grid-cols-3 gap-4 mt-4">
                                {[
                                    ["informational", "Informational", "text-sky-700"],
                                    ["commercial", "Commercial intent", "text-emerald-700"],
                                    ["long_tail", "Long-tail", "text-purple-700"],
                                ].map(([key, label, color]) => (
                                    <div key={key} className="border border-stone-200 p-3">
                                        <div className={`text-[10px] uppercase tracking-widest mb-2 ${color}`}>{label}</div>
                                        <ul className="space-y-1 text-sm">
                                            {(keywordIdeas[key] || []).map((k, i) => (
                                                <li key={i} className="border-b border-stone-100 pb-1" data-testid={`kw-${key}-${i}`}>{k}</li>
                                            ))}
                                            {(keywordIdeas[key] || []).length === 0 && <li className="text-xs text-stone-400">—</li>}
                                        </ul>
                                    </div>
                                ))}
                            </div>
                        )}
                        {keywordIdeas?.notes && <p className="text-xs text-amber-700 mt-2">{keywordIdeas.notes}</p>}
                    </div>
                </TabsContent>
            </Tabs>
        </div>
    );
}

function Field({ label, value, onChange, placeholder, textarea, testId, max }) {
    return (
        <div>
            <label className="text-[10px] uppercase tracking-widest text-stone-500 flex items-center justify-between">
                <span>{label}</span>
                {max && <span className={`text-[10px] ${(value || "").length > max ? "text-red-600" : "text-stone-400"}`}>{(value || "").length}/{max}</span>}
            </label>
            {textarea ? (
                <Textarea rows={3} value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} className="rounded-none border-stone-300 mt-1" data-testid={testId} />
            ) : (
                <Input value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} className="rounded-none border-stone-300 mt-1" data-testid={testId} />
            )}
        </div>
    );
}
